import React, { useContext, useEffect, useState } from "react";
import InformationCreditsModal from "../InformationCreditsModal";
import SourAlert from "../elements/SourAlert";
// Components
import Card from "../elements/Card";

// API
import { plansService, userService } from "../../api";
import { UserContext } from "../../contexts/UserContext";
import useSourAlert from "../../hooks/useSourAlert";

function MembershipPlans(props) {
	const user = useContext(UserContext);
	const [plans, setPlans] = useState([]);
	const [plansByReportsNumber, setPlansByReportsNumber] = useState([]);
	const [activeTab, setActiveTab] = useState("subscription");
	const [recurring, setRecurring] = useState("month");
	const [isOpenModal, setIsOpenModal] = useState(false);
	const [isSendingVerification, setIsSendingVerification] = useState(false);
	const [alertProps, showAlert] = useSourAlert();
	const requiresVerification = Boolean(user) && !user.email_verified;

	useEffect(() => {
		plansService
			.list()
			.then((response) => {
				const allPlans = response.results;
				const subscriptions = allPlans
					.filter((plan) => plan.plan_type === "subscription")
					.sort((a, b) => a.price - b.price);
				const oneTime = allPlans
					.filter((plan) => plan.plan_type !== "subscription")
					.sort((a, b) => a.price - b.price);

				setPlans(subscriptions);
				setPlansByReportsNumber(oneTime);
			})
			.catch((error) => {
				console.error(error);
			});
	}, []);

	const handlePlanSelection = (body) => {
		if (requiresVerification) {
			return;
		}

		props.onUpdate(body, 2);
	};

	const handleResendVerificationEmail = async () => {
		if (isSendingVerification) {
			return;
		}

		setIsSendingVerification(true);
		try {
			const response = await userService.resendVerificationEmail();
			showAlert({
				type: "success",
				title: "Verification email sent",
				description:
					response.detail ||
					"Check your inbox for the new verification email.",
				okButtonText: "Accept",
			});
		} catch (error) {
			showAlert({
				type: "error",
				title: "Unable to resend email",
				description:
					error.detail ||
					"An error occurred while sending the verification email.",
				okButtonText: "Ok",
			});
		} finally {
			setIsSendingVerification(false);
		}
	};

	const createCards = () => {
		let content = [];
		let pnum = 1;

		if (activeTab === "subscription") {
			for (const plan of plans) {
				if (plan.interval !== recurring) {
					continue;
				}

				content.push(
					<Card
						id={plan.id}
						key={plan.id}
						name={plan.name}
						pnum={pnum}
						price={plan.price}
						membershipName={plan.name}
						description={plan.structured_info?.tokens_included}
						discount={plan.structured_info?.discount}
						freq={plan.interval}
						currency={plan.currency || "aud"}
						onUpdate={handlePlanSelection}
						type="new"
						features={plan.structured_info?.additional_benefits}
						n_reports={plan.amount}
					/>
				);
				pnum += 1;
			}
			return content;
		} else {
			let contentTwo = [];
			for (const plan of plansByReportsNumber) {
				contentTwo.push(
					<Card
						id={plan.id}
						key={plan.id}
						small={true}
						name={plan.name}
						final_price={plan.price}
						n_reports={plan.amount}
						onUpdate={handlePlanSelection}
						pnum={pnum}
					/>
				);
				pnum += 1;
			}
			return contentTwo;
		}
	};

	const handleFilterPlan = (e) => {
		setRecurring(e.target.value);
	};

	const handleTab = (activeTab) => {
		setActiveTab(activeTab);
	};

	const frequencyOptions = (
		<>
			<option value="month">Monthly</option>
			<option value="year">Annual</option>
		</>
	);

	return (
		<React.Fragment>
			<div className="center">
				<h2 className="huge-letters is-black">
					Select <span className="is-bold">a Plan</span>
					<i
						className="fas fa-question-circle cursor help-icon huge-medium ml-5 mt-20"
						onClick={() => setIsOpenModal(true)}
					></i>
				</h2>
				{requiresVerification ? (
					<div className="membership-verification-banner">
						<span>
							Verify your email before starting a Stripe purchase.
						</span>{" "}
						<button
							type="button"
							className="membership-verification-banner__link"
							onClick={handleResendVerificationEmail}
							disabled={isSendingVerification}
						>
							{isSendingVerification
								? "Sending..."
								: "Resend verification email"}
						</button>
					</div>
				) : null}
				<div className="flexer center flexer-mobile">
					<div className="tabs">
						<button
							className={`caps ${
								activeTab === "subscription"
									? "activeTab"
									: "tab"
							}`}
							onClick={() => handleTab("subscription")}
						>
							Subscription
						</button>
						<button
							className={`caps ${
								activeTab === "oneTime" ? "activeTab" : "tab"
							}`}
							onClick={() => handleTab("oneTime")}
						>
							Packages
						</button>
					</div>
				</div>
				{activeTab === "subscription" && (
					<div className="flexer center flexer-mobile">
						<div className="frequency-container">
							<select
								className="frequency-select"
								onChange={handleFilterPlan}
							>
								{frequencyOptions}
							</select>
						</div>
					</div>
				)}
				<div className="flexer center">{createCards()}</div>
			</div>
			<InformationCreditsModal
				isOpenModal={isOpenModal}
				setIsOpenModal={setIsOpenModal}
			/>
			<SourAlert {...alertProps} />
		</React.Fragment>
	);
}

export default MembershipPlans;
